import type { AccountStoryDocument } from '@private-fiction/story-core';
import { projectAccountBot, type AppBot } from '../data/library';

export type HubPage = 'feed' | 'home' | 'chats' | 'profile' | 'studio';
export type Route = { kind: 'hub'; page: HubPage } | { kind: 'bot'; bot: AppBot } | { kind: 'create'; bot: AppBot; startSceneId?: string } | { kind: 'editor'; bot?: AppBot } | { kind: 'story'; bot: AppBot; session?: AccountStoryDocument };
export type RouteAddress = { page: HubPage } | { page: 'overview' | 'create' | 'play' | 'studio'; id: string; collectionId?: string; startSceneId?: string };

export function parseRouteAddress(hash: string): RouteAddress | null {
  if (!hash || hash === '#' || hash === '#/') return { page: 'home' };
  try {
    const [path, query = ''] = hash.replace(/^#/, '').split('?');
    if (!path?.startsWith('/')) return null;
    const parts = path.slice(1).split('/').map(decodeURIComponent);
    if (parts.some(part => !part || /[\x00-\x1f\/\\]/.test(part))) return null;
    const page = parts[0]!;
    if (parts.length === 1 && ['home', 'feed', 'chats', 'profile', 'studio'].includes(page)) return { page: page as HubPage };
    if (!['overview', 'create', 'play', 'studio'].includes(page) || !parts[1]) return null;
    if (parts.length !== 2 && !(page === 'overview' && parts.length === 4 && parts[2] === 'collection')) return null;
    const scene = new URLSearchParams(query).get('scene');
    return { page: page as 'overview' | 'create' | 'play' | 'studio', id: parts[1], ...(parts[3] ? { collectionId: parts[3] } : {}), ...(page === 'create' && scene ? { startSceneId: scene } : {}) };
  } catch { return null; }
}

export function browserRoutePath(route: Route): string {
  if (route.kind === 'hub') return `#/${route.page}`;
  if (route.kind === 'editor') return `#/studio/${encodeURIComponent(route.bot?.id ?? 'new')}`;
  if (route.kind === 'story') return `#/play/${encodeURIComponent(route.session?.id ?? route.bot.id)}`;
  return `#/${route.kind === 'bot' ? 'overview' : 'create'}/${encodeURIComponent(route.bot.id)}${route.kind === 'create' && route.startSceneId ? `?scene=${encodeURIComponent(route.startSceneId)}` : ''}`;
}

/** Resolve only from this account's loaded library. A URL never authorizes a fetch or creates a session. */
export function resolveLibraryRoute(hash: string, bots: readonly AppBot[], sessions: readonly AccountStoryDocument[], canCreate: boolean, allowDemoPlay: boolean): Route | null {
  const address = parseRouteAddress(hash);
  if (!address) return null;
  if (!('id' in address)) return { kind: 'hub', page: address.page };
  if (address.page === 'studio' && address.id === 'new') return canCreate ? { kind: 'editor' } : null;
  if (address.page === 'play') {
    const session = sessions.find(item => item.id === address.id);
    if (session) {
      const bot = bots.find(item => item.id === session.sourceBotId) ?? projectAccountBot({ ...session, id: session.sourceBotId || session.id, isBotTemplate: true }, 0, sessions);
      return { kind: 'story', bot, session };
    }
    const demo = allowDemoPlay ? bots.find(item => item.id === address.id && !item.document) : undefined;
    return demo ? { kind: 'story', bot: demo } : null;
  }
  const bot = bots.find(item => item.id === address.id);
  if (!bot) return null;
  if (address.page === 'studio') return canCreate && bot.document?.creatorOwned ? { kind: 'editor', bot } : null;
  if (address.page === 'create') return canCreate ? { kind: 'create', bot, ...(address.startSceneId && bot.document?.startScenes.some(scene => scene.id === address.startSceneId && scene.enabled) ? { startSceneId: address.startSceneId } : {}) } : null;
  return { kind: 'bot', bot };
}
